import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, MapPin } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { VIDEOS } from "@/content";

export default function Videos() {
  const [active, setActive] = useState(null);

  return (
    <section id="videos" data-testid="videos-section" className="relative py-20 lg:py-32 bg-card2/40">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(0,240,255,0.06),transparent_50%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Live Moments"
          title={<>QT 2hype <span className="text-neon">Live</span></>}
          sub="Official remixes, road mixes and live moments — straight from QT 2hype's YouTube. Press play and feel the room."
        />

        <div className="grid md:grid-cols-2 gap-6">
          {VIDEOS.map((v, i) => (
            <Reveal key={v.id} delay={(i % 2) * 0.12}>
              <button
                data-testid={`video-card-${v.id}`}
                onClick={() => setActive(v)}
                className="group relative w-full text-left rounded-2xl overflow-hidden glass hover:border-mag/50 hover:shadow-[0_0_35px_rgba(255,0,85,0.15)] transition-all"
              >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={`https://i.ytimg.com/vi/${v.youtubeId}/hqdefault.jpg`}
                    alt={`${v.title} video thumbnail`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-16 h-16 rounded-full bg-volt text-ink flex items-center justify-center shadow-[0_0_35px_rgba(204,255,0,0.45)] group-hover:scale-110 transition-transform">
                      <Play className="w-7 h-7 ml-1" />
                    </span>
                  </span>
                  {v.duration && (
                    <span className="absolute bottom-4 right-4 font-mono text-xs bg-ink/80 px-2.5 py-1 rounded-md text-white">
                      {v.duration}
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-head font-extrabold uppercase tracking-tight text-lg">{v.title}</h3>
                  <p className="mt-2 flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-500">
                    <MapPin className="w-3.5 h-3.5 text-mag" /> {v.venue}
                  </p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="video-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setActive(null)}
          >
            <button
              data-testid="video-modal-close"
              aria-label="Close video"
              className="absolute top-5 right-5 w-12 h-12 rounded-full glass flex items-center justify-center text-white hover:text-volt transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="aspect-video rounded-xl overflow-hidden shadow-[0_0_60px_rgba(255,0,85,0.2)]">
                <iframe
                  title={active.title}
                  src={`https://www.youtube-nocookie.com/embed/${active.youtubeId}?autoplay=1&rel=0`}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <p className="font-head font-bold uppercase tracking-wide text-sm text-zinc-300">{active.title}</p>
                <a
                  href={`https://www.youtube.com/watch?v=${active.youtubeId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="video-modal-youtube-link"
                  className="shrink-0 inline-flex items-center gap-2 bg-volt text-ink font-head font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-full hover:shadow-[0_0_25px_rgba(204,255,0,0.5)] transition-shadow"
                >
                  <Play className="w-3.5 h-3.5" /> Watch on YouTube
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
